import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  Button,
  IconButton,
  Tooltip,
  Chip,
} from "@mui/material";
import DashboardIcon from "@mui/icons-material/Dashboard";
import CalculateIcon from "@mui/icons-material/Calculate";
import BusinessIcon from "@mui/icons-material/Business";
import LogoutIcon from "@mui/icons-material/Logout";
import { useSetRecoilState } from "recoil";
import { authState } from "../../recoil/authAtom";
import { logout } from "../../utils/api";

const OrgNavbar = ({ onOpenCenter = null }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const setAuth = useSetRecoilState(authState);

  const orgName =
    localStorage.getItem("orgName") ||
    localStorage.getItem("orgOwnerName") ||
    "Organization Portal";

  const isDashboard =
    location.pathname === "/org-dashboard" || location.pathname === "/";
  const isCalculation = location.pathname === "/calculation";

  const handleLogout = async () => {
    try {
      await logout();
    } catch (e) {
      console.warn("Logout error:", e);
    }
    localStorage.clear();
    sessionStorage.clear();
    setAuth(null);
    navigate("/org-login", { replace: true });
  };

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        background:
          "linear-gradient(90deg, #0a1f44 0%, #0d2654 60%, #172d5c 100%)",
        borderBottom: "1px solid rgba(255,255,255,0.08)",
        boxShadow: "0 4px 20px rgba(10,31,68,0.25)",
        zIndex: (theme) => theme.zIndex.drawer + 1,
      }}
    >
      <Toolbar
        sx={{
          minHeight: "62px !important",
          px: { xs: 2, sm: 3 },
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        {/* Brand / Logo */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            cursor: "pointer",
            userSelect: "none",
          }}
          onClick={() => navigate("/org-dashboard")}
        >
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: "10px",
              background: "linear-gradient(135deg, #2a54a6 0%, #b93acf 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 4px 12px rgba(185,58,207,0.35)",
            }}
          >
            <BusinessIcon sx={{ color: "#ffffff", fontSize: "1.25rem" }} />
          </Box>
          <Box>
            <Typography
              sx={{
                fontSize: "1.05rem",
                fontWeight: 800,
                color: "#ffffff",
                lineHeight: 1.15,
                letterSpacing: "0.2px",
              }}
            >
              LITSS{" "}
              <span style={{ color: "#c084fc", fontWeight: 600 }}>ORG</span>
            </Typography>
            <Typography
              sx={{
                fontSize: "0.68rem",
                color: "rgba(255,255,255,0.6)",
                maxWidth: 180,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {orgName}
            </Typography>
          </Box>
        </Box>

        {/* 2 Primary Navigation Menus: Dashboard & Calculation */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            background: "rgba(255,255,255,0.06)",
            p: 0.5,
            borderRadius: "12px",
            border: "1px solid rgba(255,255,255,0.08)",
          }}
        >
          <Button
            onClick={() => navigate("/org-dashboard")}
            startIcon={
              <DashboardIcon
                sx={{
                  fontSize: "1.1rem !important",
                  color: isDashboard ? "#ffffff" : "rgba(255,255,255,0.7)",
                }}
              />
            }
            sx={{
              px: 2.2,
              py: 0.75,
              borderRadius: "9px",
              fontSize: "0.85rem",
              fontWeight: 700,
              textTransform: "none",
              color: isDashboard ? "#ffffff" : "rgba(255,255,255,0.7)",
              background: isDashboard
                ? "linear-gradient(135deg, #2a54a6 0%, #6D28D9 100%)"
                : "transparent",
              boxShadow: isDashboard
                ? "0 4px 12px rgba(109,40,217,0.3)"
                : "none",
              border: isDashboard
                ? "1px solid rgba(255,255,255,0.15)"
                : "1px solid transparent",
              transition: "all 0.2s ease",
              "&:hover": {
                background: isDashboard
                  ? "linear-gradient(135deg, #2a54a6 0%, #6D28D9 100%)"
                  : "rgba(255,255,255,0.1)",
                color: "#ffffff",
              },
            }}
          >
            Dashboard
          </Button>

          <Button
            onClick={() => navigate("/calculation")}
            startIcon={
              <CalculateIcon
                sx={{
                  fontSize: "1.1rem !important",
                  color: isCalculation ? "#ffffff" : "rgba(255,255,255,0.7)",
                }}
              />
            }
            sx={{
              px: 2.2,
              py: 0.75,
              borderRadius: "9px",
              fontSize: "0.85rem",
              fontWeight: 700,
              textTransform: "none",
              color: isCalculation ? "#ffffff" : "rgba(255,255,255,0.7)",
              background: isCalculation
                ? "linear-gradient(135deg, #6D28D9 0%, #b93acf 100%)"
                : "transparent",
              boxShadow: isCalculation
                ? "0 4px 12px rgba(185,58,207,0.35)"
                : "none",
              border: isCalculation
                ? "1px solid rgba(255,255,255,0.15)"
                : "1px solid transparent",
              transition: "all 0.2s ease",
              "&:hover": {
                background: isCalculation
                  ? "linear-gradient(135deg, #6D28D9 0%, #b93acf 100%)"
                  : "rgba(255,255,255,0.1)",
                color: "#ffffff",
              },
            }}
          >
            Calculation
          </Button>
        </Box>

        {/* Right side actions */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          {onOpenCenter && (
            <Tooltip title="Manage Centers" arrow>
              <Chip
                icon={
                  <BusinessIcon
                    sx={{ color: "#c084fc !important", fontSize: "0.95rem" }}
                  />
                }
                label="Center Management"
                onClick={onOpenCenter}
                clickable
                size="small"
                sx={{
                  display: { xs: "none", md: "inline-flex" },
                  bgcolor: "rgba(255,255,255,0.08)",
                  color: "#e2e8f0",
                  fontWeight: 600,
                  fontSize: "0.75rem",
                  border: "1px solid rgba(255,255,255,0.14)",
                  "&:hover": {
                    bgcolor: "rgba(255,255,255,0.14)",
                  },
                }}
              />
            </Tooltip>
          )}

          <Tooltip title="Logout" arrow>
            <IconButton
              onClick={handleLogout}
              size="small"
              sx={{
                width: 36,
                height: 36,
                borderRadius: "10px",
                background: "rgba(239,68,68,0.15)",
                border: "1px solid rgba(239,68,68,0.3)",
                color: "#f87171",
                "&:hover": {
                  background: "rgba(239,68,68,0.25)",
                  borderColor: "rgba(239,68,68,0.5)",
                  color: "#ef4444",
                },
                transition: "all 0.2s ease",
              }}
            >
              <LogoutIcon sx={{ fontSize: "1.1rem" }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default OrgNavbar;
